import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Archive, Trash2, Calendar, MapPin, DollarSign, GripVertical } from "lucide-react";
import { format } from "date-fns";
import { DroppableColumn } from "@/components/DroppableColumn";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface JobApplication {
  id: string;
  company_name: string;
  job_title: string;
  status: string;
  notes?: string;
  location?: string;
  salary_range?: string;
  applied_date?: string;
  interview_date?: string;
  created_at: string;
}

const statusColumns = [
  { id: "saved", title: "Saved", description: "Jobs saved from extensions or scan reports", color: "bg-slate-500/10 border-slate-500/20" },
  { id: "applied", title: "Applied", description: "Application completed. Awaiting response", color: "bg-blue-500/10 border-blue-500/20" },
  { id: "interview", title: "Interview", description: "Invited to interview. Record details and notes", color: "bg-purple-500/10 border-purple-500/20" },
  { id: "offer", title: "Offer", description: "Interviews completed. Negotiating offer or awaiting response", color: "bg-green-500/10 border-green-500/20" },
  { id: "rejected", title: "Rejected", description: "No response or rejection. Review and adjust approach", color: "bg-red-500/10 border-red-500/20" },
];

interface SortableCardProps {
  app: JobApplication;
  onEdit: (app: JobApplication) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
}

function SortableCard({ app, onEdit, onArchive, onDelete }: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: app.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card 
        className={`group hover:shadow-lg transition-all duration-200 cursor-move bg-card/80 backdrop-blur-sm border-border/50 hover:border-primary/30 ${
          isDragging ? "shadow-2xl scale-105 rotate-2" : ""
        }`}
      >
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab active:cursor-grabbing mt-1 text-muted-foreground hover:text-primary transition-colors"
            >
              <GripVertical className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0" onClick={() => onEdit(app)}>
              <h4 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">{app.company_name}</h4>
              <p className="text-xs text-muted-foreground truncate">{app.job_title}</p>
            </div>
          </div>
          
          <div className="space-y-2 pl-8">
            {app.location && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{app.location}</span>
              </div>
            )}
            {app.salary_range && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <DollarSign className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{app.salary_range}</span>
              </div>
            )}
            {app.applied_date && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                <span>Applied: {format(new Date(app.applied_date), "MMM d, yyyy")}</span>
              </div>
            )}
            {app.interview_date && (
              <Badge variant="secondary" className="text-xs">
                Interview: {format(new Date(app.interview_date), "MMM d")}
              </Badge>
            )}
          </div>

          <div className="flex gap-2 pt-2 pl-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs hover:bg-secondary"
              onClick={() => onArchive(app.id)}
            >
              <Archive className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onDelete(app.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ApplicationTracker() {
  const [applications, setApplications] = useState<Record<string, JobApplication[]>>({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<JobApplication | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    company_name: "",
    job_title: "",
    status: "saved",
    notes: "",
    location: "",
    salary_range: "",
    applied_date: "",
    interview_date: "",
  });
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchApplications();

    // Subscribe to smart apply queue changes
    const smartApplyChannel = supabase
      .channel('smart_apply_sync')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'smart_apply_queue',
          filter: 'status=eq.applied',
        },
        async (payload: any) => {
          // Auto-sync applied jobs to application tracker
          const job = payload.new;
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          // Check if already exists in job_applications
          const { data: existing } = await supabase
            .from('job_applications')
            .select('id')
            .eq('user_id', user.id)
            .eq('company_name', job.company_name)
            .eq('job_title', job.job_title)
            .maybeSingle();

          if (!existing) {
            // Create new application record
            await supabase.from('job_applications').insert({
              user_id: user.id,
              company_name: job.company_name,
              job_title: job.job_title,
              status: 'applied',
              location: job.location,
              salary_range: job.salary,
              notes: `Applied via Smart Apply on ${new Date().toLocaleDateString()}`,
              applied_date: new Date().toISOString(),
            });
            
            toast({ title: "Job synced to Application Tracker", description: `${job.job_title} at ${job.company_name}` });
            fetchApplications();
          }
        }
      )
      .subscribe();

    // Subscribe to job_applications changes for realtime updates
    const applicationsChannel = supabase
      .channel('applications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_applications',
        },
        () => {
          fetchApplications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(smartApplyChannel);
      supabase.removeChannel(applicationsChannel);
    };
  }, []);

  const fetchApplications = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Fetch job applications
    const { data: jobApps, error: appsError } = await supabase
      .from("job_applications")
      .select("*")
      .eq("user_id", user.id)
      .neq("status", "archived")
      .order("created_at", { ascending: false });

    if (appsError) {
      toast({ variant: "destructive", title: "Error fetching applications", description: appsError.message });
      return;
    }

    // Fetch saved jobs from job search
    const { data: savedJobs, error: savedError } = await supabase
      .from("saved_jobs")
      .select("*")
      .eq("user_id", user.id)
      .order("saved_at", { ascending: false });

    if (savedError) {
      console.error("Error fetching saved jobs:", savedError);
    }

    // Convert saved jobs to job application format
    const convertedSavedJobs = (savedJobs || []).map(job => ({
      id: job.id,
      company_name: job.company,
      job_title: job.title,
      status: 'saved',
      notes: job.description || undefined,
      location: job.location || undefined,
      salary_range: job.salary || undefined,
      applied_date: undefined,
      interview_date: undefined,
      created_at: job.saved_at,
    }));

    // Combine both sources
    const allJobs = [...(jobApps || []), ...convertedSavedJobs];

    const grouped = statusColumns.reduce((acc, col) => {
      acc[col.id] = allJobs.filter(app => app.status === col.id) || [];
      return acc;
    }, {} as Record<string, JobApplication[]>);

    setApplications(grouped);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (editingApp) {
      // Check if this is from saved_jobs
      const { data: savedJob } = await supabase
        .from('saved_jobs')
        .select('*')
        .eq('id', editingApp.id)
        .maybeSingle();

      if (savedJob) {
        // Migrate to job_applications when editing
        const { error: insertError } = await supabase
          .from('job_applications')
          .insert({ ...formData, user_id: user.id });

        if (insertError) {
          toast({ variant: "destructive", title: "Error updating application", description: insertError.message });
          return;
        }

        // Remove from saved_jobs
        await supabase.from('saved_jobs').delete().eq('id', editingApp.id);
        toast({ title: "Application updated successfully" });
      } else {
        // Regular update for job_applications
        const { error } = await supabase
          .from("job_applications")
          .update({ ...formData, updated_at: new Date().toISOString() })
          .eq("id", editingApp.id);

        if (error) {
          toast({ variant: "destructive", title: "Error updating application", description: error.message });
          return;
        }
        toast({ title: "Application updated successfully" });
      }
    } else {
      const { error } = await supabase
        .from("job_applications")
        .insert([{ ...formData, user_id: user.id }]);

      if (error) {
        toast({ variant: "destructive", title: "Error adding application", description: error.message });
        return;
      }
      toast({ title: "Application added successfully" });
    }

    setIsDialogOpen(false);
    setEditingApp(null);
    setFormData({
      company_name: "",
      job_title: "",
      status: "saved",
      notes: "",
      location: "",
      salary_range: "",
      applied_date: "",
      interview_date: "",
    });
    fetchApplications();
  };

  const handleEdit = (app: JobApplication) => {
    setEditingApp(app);
    setFormData({
      company_name: app.company_name,
      job_title: app.job_title,
      status: app.status,
      notes: app.notes || "",
      location: app.location || "",
      salary_range: app.salary_range || "",
      applied_date: app.applied_date ? format(new Date(app.applied_date), "yyyy-MM-dd") : "",
      interview_date: app.interview_date ? format(new Date(app.interview_date), "yyyy-MM-dd") : "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    // Try deleting from both tables (one will succeed)
    const { error: appError } = await supabase
      .from("job_applications")
      .delete()
      .eq("id", id);

    const { error: savedError } = await supabase
      .from("saved_jobs")
      .delete()
      .eq("id", id);

    if (appError && savedError) {
      toast({ variant: "destructive", title: "Error deleting application" });
      return;
    }
    toast({ title: "Application deleted successfully" });
    fetchApplications();
  };

  const handleArchive = async (id: string) => {
    // Check if it's a saved_job first
    const { data: savedJob } = await supabase
      .from('saved_jobs')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (savedJob) {
      // Delete from saved_jobs instead of archiving
      await supabase.from('saved_jobs').delete().eq('id', id);
      toast({ title: "Job removed from tracker" });
    } else {
      // Archive job_application
      const { error } = await supabase
        .from("job_applications")
        .update({ status: "archived" })
        .eq("id", id);

      if (error) {
        toast({ variant: "destructive", title: "Error archiving application", description: error.message });
        return;
      }
      toast({ title: "Application archived successfully" });
    }
    fetchApplications();
  };

  const handleStatusChange = async (appId: string, newStatus: string) => {
    // Check if this is a saved_job (from job search) or a job_application
    const allJobs = Object.values(applications).flat();
    const app = allJobs.find(a => a.id === appId);
    
    if (!app) return;

    // Check if it exists in saved_jobs table
    const { data: savedJob } = await supabase
      .from('saved_jobs')
      .select('*')
      .eq('id', appId)
      .maybeSingle();

    if (savedJob) {
      // This is from saved_jobs, migrate it to job_applications
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error: insertError } = await supabase
        .from('job_applications')
        .insert({
          user_id: user.id,
          company_name: app.company_name,
          job_title: app.job_title,
          status: newStatus,
          location: app.location,
          salary_range: app.salary_range,
          notes: app.notes,
          applied_date: newStatus === 'applied' ? new Date().toISOString() : undefined,
        });

      if (insertError) {
        toast({ variant: "destructive", title: "Error updating status", description: insertError.message });
        return;
      }

      // Remove from saved_jobs since it's now tracked in job_applications
      await supabase.from('saved_jobs').delete().eq('id', appId);
    } else {
      // Regular job_application update
      const { error } = await supabase
        .from('job_applications')
        .update({ 
          status: newStatus,
          applied_date: newStatus === 'applied' && !app.applied_date ? new Date().toISOString() : app.applied_date,
        })
        .eq('id', appId);

      if (error) {
        toast({ variant: "destructive", title: "Error updating status", description: error.message });
        return;
      }
    }
    
    fetchApplications();
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) return;

    // Visual feedback during drag
    const overId = over.id as string;
    const isOverColumn = statusColumns.some(col => col.id === overId);
    
    if (isOverColumn) {
      // Can add visual feedback here if needed
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find which column the card is being dropped into
    const activeColumn = Object.keys(applications).find(key =>
      applications[key].some(app => app.id === activeId)
    );

    // Check if overId is a status column
    const isDroppedOnColumn = statusColumns.some(col => col.id === overId);
    const targetColumn = isDroppedOnColumn ? overId : Object.keys(applications).find(key =>
      applications[key].some(app => app.id === overId)
    );

    if (!activeColumn || !targetColumn) return;

    // If dropped in a different column, update status
    if (activeColumn !== targetColumn) {
      await handleStatusChange(activeId, targetColumn);
      toast({ title: "Status updated", description: `Application moved to ${statusColumns.find(c => c.id === targetColumn)?.title}` });
    }
  };

  const activeApp = activeId
    ? Object.values(applications).flat().find(app => app.id === activeId)
    : null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="h-10 w-64 bg-muted animate-pulse rounded mb-2" />
              <div className="h-4 w-96 bg-muted animate-pulse rounded" />
            </div>
            <div className="h-10 w-36 bg-muted animate-pulse rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-96 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-gradient-to-br from-background to-muted/20 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Application Tracker
            </h1>
            <p className="text-muted-foreground mt-2">Drag and drop applications to update their status</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="lg"
                className="shadow-lg hover:shadow-xl transition-shadow"
                onClick={() => {
                  setEditingApp(null);
                  setFormData({
                    company_name: "",
                    job_title: "",
                    status: "saved",
                    notes: "",
                    location: "",
                    salary_range: "",
                    applied_date: "",
                    interview_date: "",
                  });
                }}>
                <Plus className="mr-2 h-5 w-5" />
                Add Application
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingApp ? "Edit Application" : "Add New Application"}</DialogTitle>
                <DialogDescription>
                  {editingApp ? "Update the details of your job application" : "Add a new job application to track"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title *</Label>
                    <Input
                      id="job_title"
                      value={formData.job_title}
                      onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Remote, New York, NY"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salary_range">Salary Range</Label>
                    <Input
                      id="salary_range"
                      value={formData.salary_range}
                      onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                      placeholder="e.g., $80k - $100k"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="applied_date">Applied Date</Label>
                    <Input
                      id="applied_date"
                      type="date"
                      value={formData.applied_date}
                      onChange={(e) => setFormData({ ...formData, applied_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interview_date">Interview Date</Label>
                    <Input
                      id="interview_date"
                      type="date"
                      value={formData.interview_date}
                      onChange={(e) => setFormData({ ...formData, interview_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background"
                  >
                    {statusColumns.map((col) => (
                      <option key={col.id} value={col.id}>{col.title}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder="Add any notes about this application..."
                  />
                </div>
                <DialogFooter>
                  <Button type="submit">{editingApp ? "Update Application" : "Add Application"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {statusColumns.map((column) => (
              <div key={column.id} className="flex flex-col min-h-[600px]">
                <Card className={`mb-4 ${column.color} border-2`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-bold">{column.title}</CardTitle>
                      <Badge variant="secondary" className="text-xs">
                        {applications[column.id]?.length || 0}
                      </Badge>
                    </div>
                    <CardDescription className="text-xs leading-relaxed">{column.description}</CardDescription>
                  </CardHeader>
                </Card>
                
                <DroppableColumn id={column.id} className="flex-1">
                  <SortableContext
                    items={applications[column.id]?.map(app => app.id) || []}
                    strategy={verticalListSortingStrategy}
                    id={column.id}
                  >
                    <div className="space-y-3 h-full p-2 rounded-lg bg-muted/20 min-h-[400px]">
                      {applications[column.id]?.map((app) => (
                        <SortableCard
                          key={app.id}
                          app={app}
                          onEdit={handleEdit}
                          onArchive={handleArchive}
                          onDelete={handleDelete}
                        />
                      ))}
                      {applications[column.id]?.length === 0 && (
                        <Card className="border-dashed border-2 bg-transparent hover:bg-muted/10 transition-colors h-full flex items-center justify-center">
                          <CardContent className="p-12 text-center">
                            <p className="text-sm text-muted-foreground font-medium">Drop applications here</p>
                            <p className="text-xs text-muted-foreground mt-1">or click + to add manually</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </SortableContext>
                </DroppableColumn>
              </div>
            ))}
          </div>
          
          <DragOverlay>
            {activeApp ? (
              <Card className="rotate-3 shadow-2xl cursor-grabbing opacity-90 bg-card border-primary">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-sm">{activeApp.company_name}</h4>
                  <p className="text-xs text-muted-foreground">{activeApp.job_title}</p>
                </CardContent>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </DashboardLayout>
  );
}
